import { useEffect, useState } from 'react'
import {
  listUsers,
  createUser,
  updateUser,
  resendInvite,
  signOutUser,
  listClients,
  createClient,
} from '../../lib/api'

const CARD = { background: '#FAF7F0', border: '1px solid #D0C9B7', borderRadius: 8, padding: 'clamp(18px, 4vw, 26px)' }
const INPUT = {
  fontFamily: 'Inter, Arial, sans-serif', fontSize: 16, color: '#191917', background: '#FFFFFF',
  border: '1px solid #D0C9B7', borderRadius: 4, padding: '12px 14px',
  width: '100%', maxWidth: '100%', boxSizing: 'border-box', minHeight: 48,
}
const LABEL = { display: 'block', fontSize: 14, fontWeight: 600, color: '#003F3A', marginBottom: 6 }
const BTN = {
  background: '#00564F', color: '#FAF7F0', border: '1px solid #00564F', borderRadius: 4,
  padding: '12px 18px', minHeight: 48, fontWeight: 600, fontSize: 15,
  fontFamily: 'Inter, Arial, sans-serif', cursor: 'pointer',
}
const BTN_GHOST = { ...BTN, background: 'transparent', color: '#00564F' }

const ROLE_LABEL = { admin: 'Office Admin', family: 'Family', caregiver: 'Caregiver' }

export default function UsersTab({ currentUserId }) {
  const [users, setUsers] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [setupLink, setSetupLink] = useState(null)
  const [copied, setCopied] = useState(false)

  const [form, setForm] = useState({ fullName: '', email: '', role: 'family', clientId: '' })
  const [newClient, setNewClient] = useState('')

  const load = async () => {
    try {
      const [u, c] = await Promise.all([listUsers(), listClients()])
      setUsers(u.users)
      setClients(c.clients)
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const showLink = (path, person) => {
    setSetupLink({ url: `${window.location.origin}${path}`, person })
    setCopied(false)
  }

  const addUser = async (e) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    try {
      const res = await createUser({
        fullName: form.fullName.trim(),
        email: form.email.trim(),
        role: form.role,
        clientId: form.role === 'family' ? form.clientId || null : null,
      })
      showLink(res.setupPath, form.fullName.trim())
      setForm({ fullName: '', email: '', role: 'family', clientId: '' })
      await load()
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const patch = async (id, body) => {
    setBusy(true)
    try {
      await updateUser(id, body)
      await load()
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  const addClient = async () => {
    const name = newClient.trim()
    if (!name || busy) return
    setBusy(true)
    try {
      const { client } = await createClient({ fullName: name })
      setNewClient('')
      await load()
      setForm((f) => ({ ...f, clientId: client.id }))
      setError('')
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (loading) return <p style={{ color: '#5D645B' }}>Loading accounts…</p>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {error && (
        <div style={{ ...CARD, background: '#FBEDEA', borderColor: '#E0B4AA', color: '#8A2C1B' }} role="alert">
          {error}
        </div>
      )}

      {/* Shown once after create/reset — the only way into a new account. */}
      {setupLink && (
        <div style={{ ...CARD, background: '#EAF2EC', borderColor: '#9CBBA6' }}>
          <p style={{ margin: 0, fontWeight: 600, color: '#003F3A' }}>Setup link for {setupLink.person}</p>
          <p style={{ margin: '8px 0 0 0', fontSize: 15, color: '#3D5647' }}>
            Send this to them yourself — by text, or an email you write. It works once, expires in 7 days, and lets them
            choose their own password. Nobody at True North ever sees that password.
          </p>
          <div style={{ marginTop: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              readOnly
              value={setupLink.url}
              style={{ ...INPUT, flex: '1 1 320px' }}
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              style={BTN}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(setupLink.url)
                  setCopied(true)
                } catch {
                  setCopied(false)
                }
              }}
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
            <button type="button" style={BTN_GHOST} onClick={() => setSetupLink(null)}>Done</button>
          </div>
        </div>
      )}

      <form style={CARD} onSubmit={addUser}>
        <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 24, fontWeight: 600, color: '#003F3A', margin: '0 0 4px 0' }}>
          Add a portal account
        </h3>
        <p style={{ margin: '0 0 20px 0', fontSize: 15, color: '#5D645B' }}>
          You create the account; they set their own password from the link.
        </p>

        <div className="grid grid-2" style={{ gap: 16 }}>
          <div style={{ minWidth: 0 }}>
            <label style={LABEL} htmlFor="nu-name">Full name</label>
            <input id="nu-name" style={INPUT} value={form.fullName} onChange={(e) => setForm({ ...form, fullName: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LABEL} htmlFor="nu-email">Email</label>
            <input id="nu-email" type="email" style={INPUT} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={LABEL} htmlFor="nu-role">Role</label>
            <select id="nu-role" style={INPUT} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              <option value="family">Family — sees one client's updates</option>
              <option value="caregiver">Caregiver — sees their own shifts</option>
              <option value="admin">Office Admin — full access</option>
            </select>
          </div>
          {form.role === 'family' && (
            <div style={{ minWidth: 0 }}>
              <label style={LABEL} htmlFor="nu-client">Which client may they see?</label>
              <select id="nu-client" style={INPUT} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
                <option value="">Choose a client…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
              </select>
            </div>
          )}
        </div>

        <div style={{ marginTop: 20 }}>
          <button type="submit" style={BTN} disabled={busy}>{busy ? 'Working…' : 'Create account & get link'}</button>
        </div>
      </form>

      <div style={CARD}>
        <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 24, fontWeight: 600, color: '#003F3A', margin: '0 0 4px 0' }}>
          Clients
        </h3>
        <p style={{ margin: '0 0 16px 0', fontSize: 15, color: '#5D645B' }}>
          A client is the person receiving care. Each family account is linked to exactly one.
        </p>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <input
            style={{ ...INPUT, flex: '1 1 260px' }}
            placeholder="New client's full name"
            value={newClient}
            onChange={(e) => setNewClient(e.target.value)}
          />
          <button type="button" style={BTN} onClick={addClient} disabled={busy || !newClient.trim()}>Add client</button>
        </div>
        {clients.length === 0 ? (
          <p style={{ margin: 0, fontSize: 15, color: '#5D645B' }}>No clients yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {clients.map((c) => (
              <div
                key={c.id}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', fontSize: 15, padding: '10px 0', borderBottom: '1px solid #E5DFD1' }}
              >
                <span style={{ fontWeight: 600, color: '#003F3A' }}>{c.full_name}</span>
                <span style={{ color: '#5D645B' }}>{c.family_count} family account{c.family_count === 1 ? '' : 's'}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {users.map((u) => {
          const isSelf = u.id === currentUserId
          return (
            <div key={u.id} style={{ ...CARD, opacity: u.is_active ? 1 : 0.6 }}>
              <div className="grid" style={{ '--cols': 'minmax(0, 1.4fr) minmax(0, 1fr) auto', gap: 16, alignItems: 'center' }}>
                <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                  <div style={{ fontWeight: 600, color: '#003F3A', fontSize: 17 }}>
                    {u.full_name} {isSelf && <span style={{ fontSize: 13, color: '#5D645B' }}>(you)</span>}
                  </div>
                  <div style={{ fontSize: 15, color: '#5D645B' }}>{u.email}</div>
                  <div style={{ fontSize: 14, color: '#5D645B', marginTop: 4 }}>
                    {ROLE_LABEL[u.role]}
                    {u.client_name ? ` · ${u.client_name}` : ''}
                    {!u.has_password && u.invite_pending ? ' · invite pending' : ''}
                    {!u.has_password && !u.invite_pending ? ' · no password set' : ''}
                    {!u.is_active ? ' · deactivated' : ''}
                  </div>
                </div>

                <div style={{ minWidth: 0 }}>
                  <label style={LABEL} htmlFor={`role-${u.id}`}>Role</label>
                  <select
                    id={`role-${u.id}`}
                    style={INPUT}
                    value={u.role}
                    disabled={busy || isSelf}
                    onChange={(e) => patch(u.id, { role: e.target.value })}
                  >
                    <option value="family">Family</option>
                    <option value="caregiver">Caregiver</option>
                    <option value="admin">Office Admin</option>
                  </select>
                  {u.role === 'family' && (
                    <select
                      style={{ ...INPUT, marginTop: 8 }}
                      value={u.client_id || ''}
                      disabled={busy}
                      onChange={(e) => patch(u.id, { clientId: e.target.value || null })}
                    >
                      <option value="">No client linked</option>
                      {clients.map((c) => <option key={c.id} value={c.id}>{c.full_name}</option>)}
                    </select>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={BTN_GHOST}
                    disabled={busy || !u.is_active}
                    onClick={async () => {
                      setBusy(true)
                      try {
                        const { setupPath } = await resendInvite(u.id)
                        showLink(setupPath, u.full_name)
                        await load()
                      } catch (err) {
                        setError(err.message)
                      } finally {
                        setBusy(false)
                      }
                    }}
                  >
                    {u.has_password ? 'Reset password' : 'New invite link'}
                  </button>
                  <button
                    type="button"
                    style={BTN_GHOST}
                    disabled={busy}
                    onClick={async () => {
                      setBusy(true)
                      try {
                        await signOutUser(u.id)
                      } catch (err) {
                        setError(err.message)
                      } finally {
                        setBusy(false)
                      }
                    }}
                  >
                    Sign out everywhere
                  </button>
                  {!isSelf && (
                    <button type="button" style={BTN_GHOST} disabled={busy} onClick={() => patch(u.id, { isActive: !u.is_active })}>
                      {u.is_active ? 'Deactivate' : 'Reactivate'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
